// src/components/Navbar.js
import React, { useEffect, useMemo, useState } from "react";
import { Accordion, Navbar, Container, Button, Modal, Nav, Offcanvas } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import { Auth, Hub } from "aws-amplify";
import "./Navbar.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPaw,
  faUserSlash,
  faUserCircle,
  faPlusCircle,
  faRightFromBracket,
  faBars,
} from "@fortawesome/free-solid-svg-icons";

import "@aws-amplify/ui-react/styles.css";
import usePwaInstall from "./InstallPwaButton";
import { isCurrentUserSupport } from "../lib/authGroups";

// 🔐 SmartTag helpers
const TAG_ID_REGEX = /^LP\d{4}$/;
const normalizeTagId = (value = "") => value.trim().toUpperCase();
const isValidTagId = (tagId) => TAG_ID_REGEX.test(tagId);

function NavbarComponent({ isAuthenticated, setIsAuthenticated, hasActiveSub }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [showAuth, setShowAuth] = useState(false);
  const [prefillUsername, setPrefillUsername] = useState("");
  const [showPaywall, setShowPaywall] = useState(false);
  const [authMode, setAuthMode] = useState("signIn"); // "signIn" | "signUp"
  const [authPrompted, setAuthPrompted] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [isAdminSupport, setIsAdminSupport] = useState(false);
const { canInstall, triggerInstall, ios } = usePwaInstall();

  const goTo = (path) => {
    setShowMenu(false);
    navigate(path);
  };

  // 🔎 Accept /smarttag/LP#### or /tag/LP#### and normalize
  const tagFromPath = useMemo(() => {
    const match = location.pathname.match(/^\/(smarttag|tag)\/(LP\d{3,})$/i);
    return match ? normalizeTagId(match[2]) : "";
  }, [location.pathname]);

useEffect(() => {
  let mounted = true;

  const refreshRole = async () => {
    const ok = await isCurrentUserSupport();
    if (mounted) setIsAdminSupport(ok);
  };

  refreshRole();

  const listener = ({ payload }) => {
    if (payload.event === "signIn" || payload.event === "signOut") {
      refreshRole();
    }
  };

  Hub.listen("auth", listener);

  return () => {
    mounted = false;
    Hub.remove("auth", listener);
  };
}, []);

  // Auto-open auth ONCE if they land on a smarttag deep link while logged out
  useEffect(() => {
    if (!tagFromPath) return;
    if (isAuthenticated) return;
    if (authPrompted) return;

    setPrefillUsername(normalizeTagId(tagFromPath));
    setShowAuth(true);
    setAuthPrompted(true);
  }, [tagFromPath, isAuthenticated, authPrompted]);

  // Close auth modal if user becomes authenticated
  useEffect(() => {
    if (isAuthenticated && showAuth) {
      setShowAuth(false);
    }
  }, [isAuthenticated, showAuth]);

  // Close auth modal if the user navigates away from a smarttag route
  useEffect(() => {
    const smarttagMatch = /^\/(smarttag|tag)\/(LP\d{3,})$/i.test(
      location.pathname
    );
    if (!smarttagMatch && showAuth) {
      setShowAuth(false);
    }
  }, [location.pathname, showAuth]);

  // Keep modal state in sync with Amplify auth events
  useEffect(() => {
    const sub = Hub.listen("auth", async ({ payload }) => {
      if (payload.event === "signIn") {
        const user = payload.data ?? (await Auth.currentAuthenticatedUser());

        const rawTagId = user?.attributes?.preferred_username || "";
        const tagId = normalizeTagId(rawTagId);

        if (!isValidTagId(tagId)) {
          console.warn(
            "User signed in without a valid SmartTag ID in preferred_username:",
            rawTagId
          );
          localStorage.removeItem("lpp.tagId");
          localStorage.removeItem("lpp.onboard");

          setIsAuthenticated?.(true);
          setShowAuth(false);
          navigate("/", { replace: true });
          return;
        }

        // Valid SmartTag owner
        localStorage.setItem("lpp.tagId", tagId);
        localStorage.setItem("lpp.onboard", "1");

        setIsAuthenticated?.(true);
        setShowAuth(false);

      }

      if (payload.event === "signOut") {
        setIsAuthenticated?.(false);
        localStorage.removeItem("lpp.tagId");
        localStorage.removeItem("lpp.onboard");
      }
    });

    return () => sub();
  }, [setIsAuthenticated, navigate, setShowAuth]);

  const handleLogout = async () => {
    await Auth.signOut();
    setIsAuthenticated(false);
    localStorage.removeItem("lpp.tagId");
    localStorage.removeItem("lpp.onboard");
    setShowMenu(false);
    navigate("/");
  };

  useEffect(() => {
    const fn = () => setShowPaywall(true);
    window.addEventListener("lpp:showPaywall", fn);
    return () => window.removeEventListener("lpp:showPaywall", fn);
  }, []);

  // 🔹 New helper: send user to /subscribe from paywall
  const handleGoToSubscribe = () => {
    setShowPaywall(false);
    setShowMenu(false);
    navigate("/subscribe");
  };

  return (
    <>
      {/* TOP NAV BAR */}
  <Navbar bg="dark" variant="dark" expand={false} className="lpp-navbar">
  <Container
    fluid
    className="lpp-navbar-inner d-flex flex-column align-items-stretch"
  >
    {/* Top row: hamburger + centered brand */}
    <div className="d-flex align-items-center justify-content-between w-100">
      <Navbar.Toggle
        aria-controls="lpp-offcanvas-menu"
        className="lpp-navbar-toggle"
        onClick={() => setShowMenu(true)}
      >
        <FontAwesomeIcon icon={faBars} className="lpp-hamburger-icon" />
      </Navbar.Toggle>

      <Navbar.Brand
  onClick={() => goTo(isAuthenticated ? "/profile" : "/")}
        className="lpp-brand d-flex align-items-center mx-auto"
        style={{ cursor: "pointer" }}
      >
        <span className="lpp-brand-gold">Little Paws Place™</span>
      </Navbar.Brand>

      {/* Spacer to balance layout on the right side */}
      <div style={{ width: "2.5rem" }} />
    </div>

    {/* Second row: Install button under the logo, centered */}
    <div className="d-flex justify-content-center w-100">

    </div>
  </Container>
</Navbar>


      <Navbar.Offcanvas
        id="lpp-offcanvas-menu"
        aria-labelledby="lpp-offcanvas-label"
        placement="end"
        className="lpp-offcanvas-menu"
        show={showMenu}
        onHide={() => setShowMenu(false)}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title id="lpp-offcanvas-label">
            Menu
          </Offcanvas.Title>
        </Offcanvas.Header>

       <Offcanvas.Body>
<Nav className="flex-column">

 {/* 🏠 PRIMARY (hide when logged in) */}
{!isAuthenticated && (
  <Nav.Link onClick={() => goTo("/")}>🏠 Home</Nav.Link>
)}


  {/* 🔽 COLLAPSIBLE: Explore */}
  <Accordion flush className="lpp-nav-accordion">
    <Accordion.Item eventKey="explore">
      <Accordion.Header>
        🌐 Explore
      </Accordion.Header>

      <Accordion.Body className="p-0">
        <Nav className="flex-column ms-2">

          {canInstall && (
            <Nav.Link onClick={triggerInstall}>
              📲 Install App
            </Nav.Link>
          )}

          <Nav.Link onClick={() => goTo("/about")}>
            🐾 About Little Paws Place
          </Nav.Link>

          <Nav.Link onClick={() => goTo("/subscribe")}>
            💳 Plans &amp; Pricing
          </Nav.Link>

          <Nav.Link
            href="https://x.com/i/communities/1999575877030408491"
            target="_blank"
            rel="noopener noreferrer"
          >
            💬 Community on X
          </Nav.Link>

          <Nav.Link onClick={() => goTo("/contact-support")}>
            🆘 Contact Support
          </Nav.Link>

        </Nav>
      </Accordion.Body>
    </Accordion.Item>
  </Accordion>

  {/* 🛠️ ADMIN */}
  {isAuthenticated && isAdminSupport && (
    <>
      <div className="mt-3 mb-1 small text-uppercase text-muted">
        Admin
      </div>
      <Nav.Link onClick={() => goTo("/admin")}>
        🛠️ Admin Dashboard
      </Nav.Link>
    </>
  )}

  {/* 🔒 MY PET HUB */}
  {isAuthenticated && (
    <>
      <div className="mt-3 mb-1 small text-uppercase text-muted">
        My Pet Hub
      </div>

      <Nav.Link onClick={() => goTo("/profile")}>
        🐶 My Pets &amp; Profile
      </Nav.Link>

      <Nav.Link onClick={() => goTo("/feeding")}>
        🍽️ Feeding &amp; Food
      </Nav.Link>

      {hasActiveSub && (
        <>
          <Nav.Link onClick={() => goTo("/smarttag")}>
            🏷️ My SmartTag
          </Nav.Link>

          <Nav.Link onClick={() => goTo("/health")}>
            ❤️‍🩹 Health &amp; Reminders
          </Nav.Link>

          <Nav.Link onClick={() => goTo("/notes")}>
            📝 Pet Notes &amp; Journal
          </Nav.Link>
        </>
      )}

      <div className="mt-3 mb-1 small text-uppercase text-muted">
        Account
      </div>

      <Nav.Link onClick={() => goTo("/overview")}>
        📋 Account Overview
      </Nav.Link>

      <Nav.Link onClick={handleLogout}>
        🚪 Logout
      </Nav.Link>
    </>
  )}

  {/* 🔐 LOGGED OUT */}
  {!isAuthenticated && (
    <>
      <div className="mt-3 mb-1 small text-uppercase text-muted">
        Account
      </div>
      <Nav.Link onClick={() => goTo("/login")}>
        🔐 Log in
      </Nav.Link>
      <Nav.Link onClick={() => goTo("/login")}>
        ✨ Get started
      </Nav.Link>
    </>
  )}
</Nav>

</Offcanvas.Body>

      </Navbar.Offcanvas>

      {/* Paywall modal – routes to /subscribe */}
      {!hasActiveSub && (
        <Modal
          show={showPaywall}
          onHide={() => setShowPaywall(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Activate SmartTag</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="mb-3">
              Your SmartTag AI features require an active plan. Choose the best
              option for your household on the next screen.
            </p>
            <div className="d-flex justify-content-end">
              <Button onClick={handleGoToSubscribe}>
                View plans &amp; activate
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      )}
    </>
  );
}

export default NavbarComponent;
